import { Injectable } from '@angular/core';
import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult
} from '@azure/msal-browser';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private readonly client = new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: environment.auth.authority,
      knownAuthorities: [environment.auth.knownAuthority],
      redirectUri: this.resolveRedirectUri(environment.auth.redirectUri),
      postLogoutRedirectUri: this.resolveRedirectUri(environment.auth.postLogoutRedirectUri)
    },
    cache: {
      cacheLocation: 'localStorage'
    }
  });

  private initializePromise?: Promise<void>;

  initialize(): Promise<void> {
    this.initializePromise ??= this.initializeClient();
    return this.initializePromise;
  }

  async login(): Promise<void> {
    await this.initialize();
    await this.client.loginRedirect({
      authority: environment.auth.authority,
      scopes: this.getScopes()
    });
  }

  async resetPassword(): Promise<void> {
    await this.initialize();
    const resetAuthority = environment.auth.resetAuthority?.trim();
    await this.client.loginRedirect({
      authority: resetAuthority || environment.auth.authority,
      scopes: this.getScopes()
    });
  }

  async logout(): Promise<void> {
    await this.initialize();
    await this.client.logoutRedirect();
  }

  async acquireAccessToken(): Promise<string | null> {
    await this.initialize();
    const account = this.getActiveAccount();
    if (!account) {
      return null;
    }

    try {
      const result = await this.client.acquireTokenSilent({
        account,
        authority: environment.auth.authority,
        scopes: this.getScopes()
      });
      return result.accessToken || null;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        return null;
      }

      throw error;
    }
  }

  private async initializeClient(): Promise<void> {
    await this.client.initialize();
    const redirectResult = await this.client.handleRedirectPromise();
    this.applyAuthenticationResult(redirectResult);
  }

  private applyAuthenticationResult(result: AuthenticationResult | null): void {
    if (result?.account) {
      this.client.setActiveAccount(result.account);
      return;
    }

    const account = this.client.getActiveAccount() ?? this.client.getAllAccounts()[0] ?? null;
    if (account) {
      this.client.setActiveAccount(account);
    }
  }

  private getActiveAccount(): AccountInfo | null {
    return this.client.getActiveAccount() ?? this.client.getAllAccounts()[0] ?? null;
  }

  private getScopes(): string[] {
    const scope = environment.auth.apiScope?.trim();
    if (!scope) {
      throw new Error('Missing environment.auth.apiScope configuration.');
    }

    return [scope];
  }

  private resolveRedirectUri(configured: string): string {
    const trimmed = configured?.trim();
    if (trimmed) {
      return trimmed;
    }

    return `${window.location.origin}/`;
  }
}
